import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import {
  IAdminNewsFeedListResponse,
  IAdminNewsFeedQueryParams,
  IAdminNewsFeedCommentsResponse,
  IAdminNewsFeedCommentsQueryParams,
  IAdminNewsFeedDeletePostResponse,
  IAdminNewsFeedDeleteCommentResponse,
} from '../../../interface/classveew-admin/news-feed.interface';
import { IErrorResponse } from '../../../interface/main.interface';
import { adminNewsFeedService } from '../../../services/classveew-admin/news-feed';
import { normalizeAxiosError } from '../../../utility/axiosErrorHandler';

/**
 * Fetch admin news feed posts with pagination, search, and status filter.
 * Supports infinite scroll – the slice appends results for page > 1.
 */
export const fetchAdminNewsFeedPosts = createAsyncThunk<
  IAdminNewsFeedListResponse,
  IAdminNewsFeedQueryParams,
  { rejectValue: IErrorResponse }
>('admin-news-feed/fetch-posts', async (params, { rejectWithValue }) => {
  try {
    const response = await adminNewsFeedService.fetchPosts(params);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, i18n.t('toast.failed_to_fetch_news_feed_posts', 'Failed to fetch news feed posts.')));
  }
});

/**
 * Fetch paginated comments for a specific post.
 * Accepts { postId, page, pageSize } — slice appends for page > 1.
 * GET /v2/admin/posts/:postId/comments?page=&pageSize=
 */
export const fetchPostComments = createAsyncThunk<
  IAdminNewsFeedCommentsResponse,
  { postId: string } & IAdminNewsFeedCommentsQueryParams,
  { rejectValue: IErrorResponse }
>('admin-news-feed/fetch-comments', async ({ postId, ...pagination }, { rejectWithValue }) => {
  try {
    const response = await adminNewsFeedService.fetchComments(postId, pagination);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_fetch_comments', 'Failed to fetch comments.')));
  }
});

/**
 * Soft-delete a post by ID.
 * DELETE /v2/admin/posts/:postId
 */
export const deleteAdminPost = createAsyncThunk<
  IAdminNewsFeedDeletePostResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-news-feed/delete-post', async (postId, { rejectWithValue }) => {
  try {
    const response = await adminNewsFeedService.deletePost(postId);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_delete_post', 'Failed to delete post.')));
  }
});

/**
 * Soft-delete a comment under a specific post.
 * DELETE /v2/admin/posts/:postId/comments/:commentId
 */
export const deleteAdminComment = createAsyncThunk<
  IAdminNewsFeedDeleteCommentResponse,
  { postId: string; commentId: string },
  { rejectValue: IErrorResponse }
>('admin-news-feed/delete-comment', async ({ postId, commentId }, { rejectWithValue }) => {
  try {
    const response = await adminNewsFeedService.deleteComment(postId, commentId);
    return response.data;
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.failed_to_delete_comment_2', 'Failed to delete comment.')));
  }
});

export const adminNewsFeedActions = {
  fetchAdminNewsFeedPosts,
  fetchPostComments,
  deleteAdminPost,
  deleteAdminComment,
};
